import { useBookPageStore } from 'stores/bookPage';
import { storeToRefs } from 'pinia';
import { computed, inject } from 'vue';
import BOOK_ACCESS_TYPES from 'src/enums/bookAccessTypes';
import parseLinkToEmbed from 'src/helpers/parseLinkToEmbed';

export default (data) => {
  const bus = inject('bus');
  const bookPageStore = useBookPageStore();

  const { page, pageData, book } = storeToRefs(bookPageStore);

  const colorScheme = {
    fontColor: 'colorSettingsFont',
    primaryColor: 'colorSettingsPrimary',
    borderColor: 'colorSettingsBorder',
    cardColor: 'colorSettingsCard',
    backgroundColor: 'colorSettingsBackground',
  };

  const offerButtonColorKeys = {
    backgroundColor: 'offerButtonBackgroundColor',
    textColor: 'offerButtonTextColor',
  };

  const offerColorKeys = {
    titleColor: 'offerTitleColor',
    textColor: 'offerTextColor',
    timerColor: 'offerTimerTextColor',
    backgroundColor: 'offerBackground',
  };

  const pageFields = computed(() => data || pageData.value);

  const styles = computed(() => (`
    --page-font-color: ${pageFields.value.colorSettingsFont};
    --page-primary-color: ${pageFields.value.colorSettingsPrimary};
    --page-border-color: ${pageFields.value.colorSettingsBorder};
    --page-card-color: ${pageFields.value.colorSettingsCard};
    --page-background-color: ${pageFields.value.colorSettingsBackground};
    --offer-button-background-color: ${pageFields.value.offerButtonBackgroundColor};
    --offer-button-text-color: ${pageFields.value.offerButtonTextColor};
    --offer-title-color: ${pageFields.value.offerTitleColor};
    --offer-text-color: ${pageFields.value.offerTextColor};
    --offer-timer-color: ${pageFields.value.offerTimerTextColor};
    --offer-background-color: ${pageFields.value.offerBackground};
    --h2-font-size: ${pageFields.value.fontSettingsH2FontSize}px;
    --h3-font-size: ${pageFields.value.fontSettingsH3FontSize}px;
    --h4-font-size: ${pageFields.value.fontSettingsH4FontSize}px;
    --p-font-size: ${pageFields.value.fontSettingsPFontSize}px;
    --note-font-size: ${pageFields.value.fontSettingsNoteFontSize}px;
    font-family: ${pageFields.value.fontSettingsFontFamily || 'Mitr'};
  `));

  // images
  const headerImage = computed(() => (page.value?.headerImagePreview || pageFields.value?.headerImage || '/landing/your-logo.png'));
  const promoMedia = computed(() => {
    if (pageFields.value.promoVideoUrl) {
      return parseLinkToEmbed(pageFields.value.promoVideoUrl);
    }

    return (page.value?.promoImagePreview || pageFields.value?.promoImage || page.value?.book?.coverImageUrl);
  });
  const isPromoVideo = computed(() => !!pageFields.value?.promoVideoUrl);
  const overviewImageLeft = computed(() => (page.value?.overviewImageLeftPreview || pageFields.value?.overviewImageLeft || '/landing/features-list-cover.png'));
  const overviewImageRight = computed(() => (page.value?.overviewImageRightPreview || pageFields.value?.overviewImageRight || '/landing/features-list-cover.png'));
  const featuresGridImageLaptop = computed(() => (page.value?.featuresGridLaptopImagePreview || pageFields.value?.featuresGridLaptopImage));
  const featuresGridImagePhone = computed(() => (page.value?.featuresGridPhoneImagePreview || pageFields.value?.featuresGridPhoneImage));
  const callToActionImage = computed(
    () => page.value?.callToActionImagePreview || pageFields.value?.callToActionImage || '/landing/call-to-action-cover.png',
  );
  const isMediaVideo = computed(() => !!pageFields.value?.mediaVideoUrl);
  const mediaContent = computed(() => {
    if (pageFields.value.mediaVideoUrl) {
      return parseLinkToEmbed(pageFields.value.mediaVideoUrl);
    }

    return page.value?.mediaImagePreview || pageFields.value?.mediaImage || '/landing/media-placeholder.png';
  });

  const footerImage = computed(() => (page.value?.footerImagePreview || pageFields.value?.footerImage || '/landing/your-logo.png'));
  const offerImage = computed(
    () => (page.value?.offerImagePreview || pageFields.value?.offerImage) || page.value?.book?.coverImageUrl || '/landing/call-to-action-cover.png',
  );

  // tracking codes
  const headerTrackingCode = computed(() => {
    if (page.value.trackingCodes?.headerCode) {
      return page.value.trackingCodes.headerCode;
    }

    if (book.value.trackingCodes?.headerCode) {
      return book.value.trackingCodes.headerCode;
    }

    return null;
  });
  const footerTrackingCode = computed(() => {
    if (page.value.trackingCodes?.footerCode) {
      return page.value.trackingCodes.footerCode;
    }

    if (book.value.trackingCodes?.footerCode) {
      return book.value.trackingCodes.footerCode;
    }

    return null;
  });

  function buttonAction() {
    if (book.value.access.type === BOOK_ACCESS_TYPES.PAYWALL) {
      return window.open(`https://www.checkout-ds24.com/product/${book.value.access.data.defaultProductId}`, '__blank');
    }

    if (book.value.access.type === BOOK_ACCESS_TYPES.LINK) {
      bus.emit('open-email-form');
      return;
    }

    window.open(book.value.publicUrl, '__blank');
  }

  return {
    styles,
    headerImage,
    promoMedia,
    isPromoVideo,
    overviewImageLeft,
    overviewImageRight,
    featuresGridImageLaptop,
    featuresGridImagePhone,
    callToActionImage,
    footerImage,
    buttonAction,
    colorScheme,
    offerColorKeys,
    isMediaVideo,
    mediaContent,
    headerTrackingCode,
    footerTrackingCode,
    offerImage,
    offerButtonColorKeys,
  };
};
